/**
 * Phase 14 — Buffet Lines / Islands sub-section for the Ship Settings page.
 *
 * Manages the `lines` array on the **default ship's** `lib_ships` doc.
 * If the default ship name doesn't exist yet in lib_ships (Phase-4 voyages
 * never created a row), the form auto-creates one on first save so the
 * lines have somewhere to live.
 *
 * Multi-ship: when the user has multiple ships in lib_ships, an optional
 * dropdown above the lines list lets them switch which ship they're
 * editing. Defaults to whichever ship matches `settings.default_ship_name`.
 *
 * Locked behaviors per the Phase 14 brief:
 *   - Lines are reordered via up/down buttons (drag was scoped out).
 *   - Deleting a line that's referenced on at least one voyage day
 *     triggers a confirm dialog with the affected counts. On confirm,
 *     the backend nulls out `line_id` + `line_name` on every matching
 *     station snapshot (sweep).
 *   - New stations created from a theme buffet ALWAYS start "Unassigned" —
 *     the lines library never auto-assigns.
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Plus, X, ArrowUp, ArrowDown, Ship as ShipIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import client from "@/apiClient";

export default function ShipLinesPanel({ defaultShipName }) {
  const [ships, setShips] = useState([]);
  const [shipId, setShipId] = useState(null);
  const [lines, setLines] = useState([]);
  const [originalLines, setOriginalLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Pending delete-with-sweep state.
  const [pendingDelete, setPendingDelete] = useState(null); // { line, usage }
  const [deleting, setDeleting] = useState(false);

  const dirty = useMemo(
    () => JSON.stringify(lines) !== JSON.stringify(originalLines),
    [lines, originalLines],
  );

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await client.get("/lib/ships");
        if (!alive) return;
        const arr = r.data || [];
        setShips(arr);
        // Pick the default ship by name (case-insensitive); fallback to first.
        const want = (defaultShipName || "").trim().toLowerCase();
        const matched = arr.find(
          (s) => (s.name || "").trim().toLowerCase() === want,
        );
        const picked = matched || arr[0] || null;
        if (picked) {
          setShipId(picked.id);
          setLines(picked.lines || []);
          setOriginalLines(picked.lines || []);
        } else {
          setShipId(null);
          setLines([]);
          setOriginalLines([]);
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [defaultShipName]);

  const switchShip = (newShipId) => {
    if (dirty) {
      const ok = window.confirm(
        "You have unsaved line changes. Switch ship and discard?",
      );
      if (!ok) return;
    }
    const ship = ships.find((s) => s.id === newShipId);
    if (!ship) return;
    setShipId(ship.id);
    setLines(ship.lines || []);
    setOriginalLines(ship.lines || []);
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));
  };
  const moveLine = (idx, delta) => {
    setLines((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      // Re-flow the `order` field so it matches the visible order.
      return next.map((ln, i) => ({ ...ln, order: i + 1 }));
    });
  };
  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { id: null, name: "", order: (prev.length || 0) + 1 },
    ]);
  };

  // Either delete locally (line never persisted yet) or open the confirm
  // dialog with usage counts when it IS persisted.
  const removeLine = async (idx) => {
    const line = lines[idx];
    if (!line.id) {
      // Never-persisted line — just drop it.
      setLines((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    try {
      const r = await client.get(`/lib/ships/${shipId}/lines/${line.id}/usage`);
      setPendingDelete({ line, usage: r.data });
    } catch (e) {
      toast({
        title: "Could not check usage",
        description: e?.response?.data?.detail || String(e),
        variant: "destructive",
      });
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await client.delete(
        `/lib/ships/${shipId}/lines/${pendingDelete.line.id}`,
      );
      setLines((prev) => prev.filter((ln) => ln.id !== pendingDelete.line.id));
      setOriginalLines((prev) =>
        prev.filter((ln) => ln.id !== pendingDelete.line.id),
      );
      const u = pendingDelete.usage;
      toast({
        title: "Line deleted",
        description: u.affected_stations
          ? `Swept ${u.affected_stations} station assignments across ${u.affected_days} day(s) on ${u.affected_voyages} voyage(s).`
          : "Line removed (no voyage assignments).",
      });
      setPendingDelete(null);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || String(e),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const save = async () => {
    if (!shipId) {
      // Auto-create a lib_ships row for the default ship name.
      if (!(defaultShipName || "").trim()) {
        toast({
          title: "Set a default ship name first",
          description: "Save the ship name above before adding lines.",
          variant: "destructive",
        });
        return;
      }
      setSaving(true);
      try {
        const r = await client.post("/lib/ships", {
          name: defaultShipName.trim(),
          lines: lines.filter((ln) => (ln.name || "").trim()),
        });
        const created = r.data;
        setShips((prev) => [...prev, created]);
        setShipId(created.id);
        setLines(created.lines || []);
        setOriginalLines(created.lines || []);
        toast({
          title: "Lines saved",
          description: `Created lib_ships row for "${created.name}".`,
        });
      } catch (e) {
        toast({
          title: "Save failed",
          description: e?.response?.data?.detail || String(e),
          variant: "destructive",
        });
      } finally {
        setSaving(false);
      }
      return;
    }
    setSaving(true);
    try {
      // PUT replaces the whole ship doc, so send through the name field too.
      const existing = ships.find((s) => s.id === shipId) || {};
      const cleaned = lines
        .filter((ln) => (ln.name || "").trim())
        .map((ln, i) => ({
          id: ln.id || null,
          name: ln.name.trim(),
          order: i + 1,
        }));
      const r = await client.put(`/lib/ships/${shipId}`, {
        name: existing.name,
        notes: existing.notes,
        lines: cleaned,
      });
      const updated = r.data;
      setShips((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setLines(updated.lines || []);
      setOriginalLines(updated.lines || []);
      toast({
        title: "Lines saved",
        description: `Updated lines on "${updated.name}".`,
      });
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || String(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-10 max-w-md rounded-lg border border-slate-200 bg-white/40 p-6 shadow-sm"
           data-testid="ship-lines-loading">
        <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div
      className="mt-10 max-w-2xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="ship-lines-panel"
    >
      <div className="mb-3 flex items-center gap-2 text-slate-900">
        <ShipIcon className="h-4 w-4" />
        <h2 className="text-sm font-bold tracking-wide uppercase">
          Buffet Lines / Islands
        </h2>
      </div>
      <p className="mb-4 text-xs text-slate-600 leading-relaxed">
        Per-ship list of serving lines / islands (e.g.&nbsp;
        <span className="font-mono">Line 1 Hot</span>,&nbsp;
        <span className="font-mono">Pasta Island</span>). Each station on
        a voyage day can be tagged to one of these, and the buffet PDF /
        Excel exports can re-group stations by line at print time. Theme
        buffet templates never auto-assign a line — pick per day.
      </p>

      {ships.length > 1 && (
        <div className="mb-4 space-y-1.5">
          <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-700">
            Editing ship
          </Label>
          <select
            value={shipId || ""}
            onChange={(e) => switchShip(e.target.value)}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none"
            data-testid="ship-lines-ship-select"
          >
            {ships.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900"
             data-testid="ship-lines-error">
          {error}
        </div>
      )}

      <div className="space-y-2" data-testid="ship-lines-list">
        {lines.length === 0 && (
          <p className="text-xs italic text-slate-500" data-testid="ship-lines-empty">
            No lines yet. Click "Add line" to start.
          </p>
        )}
        {lines.map((ln, i) => (
          <div
            key={ln.id || `new-${i}`}
            className="flex items-center gap-2"
            data-testid={`ship-line-row-${i}`}
          >
            <Input
              value={ln.name || ""}
              onChange={(e) => updateLine(i, { name: e.target.value })}
              placeholder="Line / Island name"
              className="flex-1"
              data-testid={`ship-line-name-${i}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={i === 0}
              onClick={() => moveLine(i, -1)}
              data-testid={`ship-line-up-${i}`}
              title="Move up"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={i === lines.length - 1}
              onClick={() => moveLine(i, +1)}
              data-testid={`ship-line-down-${i}`}
              title="Move down"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeLine(i)}
              data-testid={`ship-line-delete-${i}`}
              title="Delete line"
            >
              <X className="h-4 w-4 text-rose-600" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLine}
          data-testid="ship-lines-add-btn"
        >
          <Plus className="h-4 w-4" />
          Add line
        </Button>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-xs text-amber-600 font-medium">Unsaved</span>
          )}
          <Button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="ship-lines-save-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save lines
          </Button>
        </div>
      </div>

      {/* ─── Delete confirm with usage preview ─── */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent data-testid="ship-line-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete line "{pendingDelete?.line?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(pendingDelete?.usage?.affected_stations || 0) === 0 ? (
                <>This line is not referenced by any voyage day. Safe to remove.</>
              ) : (
                <>
                  <strong>
                    {pendingDelete?.usage?.affected_stations}
                  </strong>{" "}
                  station assignment(s) across{" "}
                  <strong>{pendingDelete?.usage?.affected_days}</strong>{" "}
                  day(s) on{" "}
                  <strong>{pendingDelete?.usage?.affected_voyages}</strong>{" "}
                  voyage(s) reference this line. On confirm they will
                  become <em>Unassigned</em> (both <code>line_id</code> and{" "}
                  <code>line_name</code> nulled in the snapshot).
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="ship-line-delete-confirm"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete & sweep"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
